import React, { useState, useLayoutEffect, Fragment, useRef } from "react"
import { useLocation, useHistory } from "react-router-dom"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import { isInKioskMode } from "utils"
import PrinterForm from "./PrinterForm"
import PrinterFooter from "./PrinterFooter"
import { ConfirmationModal, Navbar } from "components"
import PrinterFoodCategories from "./PrinterCategories"
import PrinterOtherSettings from "./PrinterOtherSettings"
import PrinterDeleteButton from "./PrinterDeleteButton"
import classes from "./index.module.scss"

const initialFormErrorsState = {
  printerName: "",
  ipAddress: "",
  port: ""
}

const initialFormState = {
  printerId: "",
  printerName: "",
  printerCutter: true,
  printerModel: "thermal",
  printerInterface: "tcp",
  macAddress: "",
  deviceName: "",
  deviceId: 0,
  pageSize: "",
  ipAddress: "",
  port: "",
  printerWidth: 80,
  printerPaperWidth: 72,
  printerResolution: 8,
  printerType: "epson",
  vendorId: 0,
  productId: 0,
  forPos: false,
  forKitchen: false,
  forQrTable: false,
  forCustomer: false,
  forShiftReport: false,
  forOrderItems: false,
  forCourierLabel: false,
  perOrderItemQuanity: false,
}

const Printer = () => {
  const [t] = useTranslation()

  const history = useHistory()

  const location = useLocation()

  const initialPrinterId =
    location.state?.printerId

  const initialPrinterIndexState =
    location.state?.initialPrinterIndex ?? null

  const initialPrinter =
    useSelector(state =>
      state.printers.find(p => p._id === initialPrinterId)
    )

  const [openSidebar, setOpenSidebar] = useState(false)
  const [form, setForm] = useState(initialFormState)
  const [printerCategoryIds, setPrinterCategoryIds] = useState({})
  const [formErrors, setFormErrors] = useState(initialFormErrorsState)
  const [printerIndex, setPrinterIndex] = useState(initialPrinterIndexState)
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false)

  const previousPrinterVariables =
    useRef(
      initialPrinter ||
      {
        ...initialFormState,
        printerCategoryIds: {}
      }
    )

  const checkUnsavedChanges = () => {
    const previous = previousPrinterVariables.current

    if (form.printerName !== previous.printerName) return false
    if (form.ipAddress !== previous.ipAddress) return false
    if (form.macAddress !== previous.macAddress) return false
    if (form.deviceName !== previous.deviceName) return false
    if (form.printerType !== previous.printerType) return false
    if (form.printerWidth !== previous.printerWidth) return false
    if (form.forKitchen !== previous.forKitchen) return false
    if (form.forQrTable !== previous.forQrTable) return false
    if (form.forShiftReport !== previous.forShiftReport) return false
    if (form.forOrderItems !== previous.forOrderItems) return false
    if (form.printerModel !== previous.printerModel) return false
    if (form.localPrintServerIpAddress !== previous.localPrintServerIpAddress) return false
    if (form.pageSize === previous.pageSize)
      if (form.printerCutter === previous.printerCutter)

        for (const categoryId in previous.printerCategoryIds) {
          if (
            previous.printerCategoryIds[categoryId] !==
            printerCategoryIds[categoryId]
          ) return false
        }

    for (const categoryId in printerCategoryIds) {
      if (
        previous.printerCategoryIds[categoryId] !==
        printerCategoryIds[categoryId]
      ) return false
    }

    return true
  }

  const handleGoBack = () => {
    if (!checkUnsavedChanges()) {
      return setShowUnsavedChangesModal(true)
    }

    history.push(
      kioskMode ? "kiosk-settings" : "/settings",
      { initialTab: "printers" }
    )
  }

  const fillForm = () => {
    if (!initialPrinter) return

    setForm({
      forPos: !!initialPrinter.forPos,
      printerId: initialPrinter._id,
      vendorId: initialPrinter.vendorId,
      productId: initialPrinter.productId,
      port: String(initialPrinter.port),
      printerName: initialPrinter.printerName,
      ipAddress: initialPrinter.ipAddress,
      deviceName: initialPrinter.deviceName,
      printerInterface: initialPrinter.printerInterface,
      macAddress: initialPrinter.macAddress,
      deviceId: initialPrinter.deviceId,
      printerType: initialPrinter.printerType,
      printerWidth: initialPrinter.printerWidth,
      printerPaperWidth: initialPrinter.printerPaperWidth,
      printerResolution: initialPrinter.printerResolution,
      forKitchen: initialPrinter.forKitchen,
      forQrTable: initialPrinter.forQrTable,
      forCustomer: initialPrinter.forCustomer,
      forShiftReport: initialPrinter.forShiftReport,
      forOrderItems: initialPrinter.forOrderItems,
      printerModel: initialPrinter.printerModel,
      pageSize: initialPrinter.pageSize || "",
      forCourierLabel: initialPrinter.forCourierLabel,
      printerCutter: initialPrinter.printerCutter,
      localPrintServerIpAddress: initialPrinter.localPrintServerIpAddress,
      perOrderItemQuanity: !!initialPrinter.perOrderItemQuanity
    })

    const printerCategoryIds = {}

    initialPrinter.categoryIds.forEach(categoryId => {
      printerCategoryIds[categoryId] = true
    })

    setPrinterCategoryIds(
      printerCategoryIds
    )

    previousPrinterVariables.current.printerCategoryIds =
      printerCategoryIds
  }

  useLayoutEffect(() => {
    fillForm()
  }, [])

  const editMode = printerIndex !== null

  const forPos = !!initialPrinter?.forPos

  const kioskMode = isInKioskMode()

  return (
    <Fragment>
      <div className={classes["container"]}>
        <Navbar
          variant="compact"
          title={t("پرینتر")}
          onLeftIconPress={handleGoBack}
          rightSideComponent={() =>
            !forPos &&
            <PrinterDeleteButton
              printerId={form.printerId}
              editMode={editMode}
              printerIndex={printerIndex}
            />
          }
        />

        <div className={classes["wrapper"]}>
          <PrinterForm
            forPos={forPos}
            form={form}
            setForm={setForm}
            formErrors={formErrors}
          />

          <PrinterOtherSettings
            form={form}
            setForm={setForm}
          />

          <PrinterFooter
            forPos={forPos}
            form={form}
            printerCategoryIds={printerCategoryIds}
            editMode={editMode}
            setFormErrors={setFormErrors}
            setOpenSidebar={setOpenSidebar}
            setPrinterIndex={setPrinterIndex}
            initialFormErrorsState={initialFormErrorsState}
            previousPrinterVariables={previousPrinterVariables}
          />
        </div>
      </div>

      <PrinterFoodCategories
        openSidebar={openSidebar}
        setOpenSidebar={setOpenSidebar}
        printerCategoryIds={printerCategoryIds}
        setPrinterCategoryIds={setPrinterCategoryIds}
      />

      <ConfirmationModal
        title={t("تغییرات_شما_ذخیره_نشده_آیا_می_خواهید_از_این_صفحه_خارج_شوید")}
        show={showUnsavedChangesModal}
        onHide={() => setShowUnsavedChangesModal(false)}
        onConfirm={() =>
          history.push(
            kioskMode ? "/kiosk-settings" : "/settings",
            { initialTab: "printers" }
          )
        }
      />
    </Fragment>
  )
}

export default Printer